/**
 * Cross-domain postMessage bridge for parent <-> iframe communication.
 *
 * Messages follow the shape: { type: string; payload?: unknown }
 *
 * ALLOWED_ORIGINS — set to '*' during development.  In production, replace
 * with the exact origin(s) of the host page(s) that embed this app.
 */

const ALLOWED_ORIGINS: string | string[] = '*'

type MessageHandler = (payload: unknown, origin: string) => void

const handlers = new Map<string, Set<MessageHandler>>()

function isAllowedOrigin(origin: string): boolean {
  if (ALLOWED_ORIGINS === '*') return true
  if (Array.isArray(ALLOWED_ORIGINS)) return ALLOWED_ORIGINS.includes(origin)
  return origin === ALLOWED_ORIGINS
}

function handleIncoming(event: MessageEvent) {
  if (!isAllowedOrigin(event.origin)) return
  const { type, payload } = event.data ?? {}
  if (typeof type !== 'string') return

  const subs = handlers.get(type)
  if (subs) {
    subs.forEach((fn) => fn(payload, event.origin))
  }
}

/** Start listening for messages from the parent window. */
export function initBridge() {
  window.addEventListener('message', handleIncoming)
}

/** Stop listening. */
export function destroyBridge() {
  window.removeEventListener('message', handleIncoming)
}

/** Subscribe to a specific message type coming from the parent. */
export function onMessage(type: string, handler: MessageHandler) {
  if (!handlers.has(type)) handlers.set(type, new Set())
  handlers.get(type)!.add(handler)
  return () => {
    handlers.get(type)?.delete(handler)
  }
}

/** Send a message to the parent window. */
export function sendToParent(type: string, payload?: unknown) {
  if (!window.parent || window.parent === window) return
  const targetOrigin = ALLOWED_ORIGINS === '*' ? '*' : ALLOWED_ORIGINS
  const origin = Array.isArray(targetOrigin) ? targetOrigin[0] : targetOrigin
  window.parent.postMessage({ type, payload }, origin)
}
